import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicySandboxPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24">
        <div className="container mx-auto px-4 py-16 max-w-3xl">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#214140' }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mb-10">Last updated: September 23, 2026</p>

          <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>1. Introduction</h2>
              <p>
                Nursetech Medical™ Corp. ("we," "us," or "our") operates the website nursetechmedical.com
                and provides medical device products and related services. This Privacy Policy explains
                how we collect, use, disclose, and safeguard your information when you visit our website
                or purchase our products.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>2. Information We Collect</h2>
              <p className="mb-3">We may collect the following types of information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Personal Information:</strong> Name, email address, phone number, mailing address, and organization name when you fill out forms, request samples, or place orders.</li>
                <li><strong>Payment Information:</strong> Credit card or payment details processed through our secure third-party payment processors. We do not store full payment card numbers on our servers.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our website, including IP address, browser type, pages visited, time spent, and referring URLs.</li>
                <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to improve your experience and analyze site traffic.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>3. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Process and fulfill orders, sample requests, and trial requests</li>
                <li>Communicate with you about products, services, and support</li>
                <li>Send promotional materials and updates (with your consent)</li>
                <li>Improve our website, products, and customer experience</li>
                <li>Comply with legal obligations and protect our rights</li>
                <li>Analyze website usage and trends</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>4. Information Sharing</h2>
              <p className="mb-3">We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Third parties who assist with payment processing, shipping, email delivery, and website analytics.</li>
                <li><strong>Distribution Partners:</strong> Authorized distributors who may fulfill orders or provide local support.</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
              </ul>
              <p className="mt-3">
                <strong>
                  We do not share, sell, or rent customer mobile phone numbers with third parties or
                  affiliates for marketing or promotional purposes.
                </strong>{' '}
                No mobile information will be shared with third parties or affiliates for marketing or
                promotional purposes. Text messaging originator opt-in data and consent are excluded from
                all of the sharing categories above and will not be shared with any third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>5. Data Security</h2>
              <p>
                We implement reasonable administrative, technical, and physical security measures to
                protect your personal information. However, no method of transmission over the Internet
                or electronic storage is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>6. Cookies</h2>
              <p>
                Our website uses cookies to enhance your browsing experience. You can control cookie
                settings through your browser preferences. Disabling cookies may limit some functionality
                of our website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>7. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the
                privacy practices or content of those sites. We encourage you to review the privacy
                policies of any third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>8. Children's Privacy</h2>
              <p>
                Our website and services are not directed to individuals under the age of 18. We do not
                knowingly collect personal information from children. If we become aware that we have
                collected information from a child, we will take steps to delete it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>9. Your Rights</h2>
              <p className="mb-3">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt out of marketing communications</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us using the information below.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>10. SMS and Phone Communications</h2>
              <p className="mb-3">
                Customers may opt in by entering their phone number at{' '}
                <a href="https://nursetechmedical.com/sample-request" className="underline">nursetechmedical.com/sample-request</a>,{' '}
                <a href="https://nursetechmedical.com/trial-request" className="underline">nursetechmedical.com/trial-request</a>, or{' '}
                <a href="https://nursetechmedical.com/contact" className="underline">nursetechmedical.com/contact</a> and
                checking a consent checkbox. The form states that they agree to receive SMS messages and phone
                calls from Nursetech Medical. The customer is also notified that message frequency may vary,
                message and data rates may apply, and that they can reply STOP to opt out or HELP for
                assistance. Links to our Terms of Service and Privacy Policy are provided on the form.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Automated and AI-assisted communications:</strong> Messages and calls may be sent or placed using automated technology, including autodialers, artificial or prerecorded voices, and AI-powered voice and text assistants acting on behalf of Nursetech Medical.</li>
                <li><strong>Message types:</strong> Follow-up on your request, sample and trial coordination, product information, support, and marketing.</li>
                <li><strong>Frequency and cost:</strong> Message frequency may vary. Message and data rates may apply.</li>
                <li><strong>Opt out:</strong> Reply STOP to any text message to stop receiving texts. You may also ask to be removed during any call, or email info[at]nursetechmedical.com.</li>
                <li><strong>Help:</strong> Reply HELP to any text message or email info[at]nursetechmedical.com.</li>
                <li><strong>Consent is optional:</strong> Consent is not a condition of purchase or of submitting any form.</li>
                <li>Carriers are not liable for delayed or undelivered messages.</li>
              </ul>
              <p className="mt-3">
                <strong>
                  We do not share, sell, or rent customer mobile phone numbers with third parties or
                  affiliates for marketing or promotional purposes.
                </strong>{' '}
                Text messaging opt-in data and consent will not be shared with any third parties.
              </p>
              <p className="mt-3">
                Mobile opt-in information, including phone numbers and SMS consent, will not be shared with
                third parties for marketing purposes. We use it only to send the messages you asked for.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page
                with an updated "Last updated" date. Your continued use of our website after changes are
                posted constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ color: '#214140' }}>12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <p className="mt-3">
                Nursetech Medical™ Corp.<br />
                8 The Green, Ste A<br />
                Dover, DE 19901<br />
                Email: legal[at]nursetechmedical.com
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicySandboxPage;
